// src/components/maps/CoordinateSelector.js
import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Button,
    TextField,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import {
    MyLocation as MyLocationIcon,
    Add as AddIcon,
    Check as CheckIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import GeographicMap from './GeographicMap';
import { parseCoordinates, isValidCoordinates, formatCoordinates } from '../../utils/coordinateUtils';

const CoordinateSelector = ({
    value = '',
    onChange,
    label = 'Coordenadas Geográficas',
    helperText = ''
}) => {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [coordinates, setCoordinates] = useState(value);
    const [isValid, setIsValid] = useState(true);

    useEffect(() => {
        setCoordinates(value);
    }, [value]);

    useEffect(() => {
        if (!coordinates) {
            setIsValid(true); // El campo vacío es neutro
            return;
        }
        const [lat, lng] = parseCoordinates(coordinates);
        setIsValid(isValidCoordinates(lat, lng));
    }, [coordinates]);

    const handleOpenDialog = () => {
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
    };

    const handleAcceptCoordinates = () => {
        const [lat, lng] = parseCoordinates(coordinates);
        if (isValidCoordinates(lat, lng)) {
            const formatted = formatCoordinates(lat, lng);
            setCoordinates(formatted);
            if (onChange) {
                onChange(formatted);
            }
            setDialogOpen(false);
        } else {
            alert('Por favor, ingrese un formato de coordenadas válido antes de aceptar.');
        }
    };

    const handleCoordinateChange = (e) => {
        const rawValue = e.target.value;
        setCoordinates(rawValue);

        // Propagar inmediatamente si el usuario escribió un par válido completo
        const [lat, lng] = parseCoordinates(rawValue);
        if (isValidCoordinates(lat, lng)) {
            if (onChange) {
                onChange(formatCoordinates(lat, lng));
            }
        } else {
            if (onChange) {
                onChange(rawValue); // Propagar valor crudo mientras escribe
            }
        }
    };

    const handleBlur = () => {
        if (!coordinates) return;

        // Auto-formatear y limpiar al perder el foco (onBlur)
        const [lat, lng] = parseCoordinates(coordinates);
        if (isValidCoordinates(lat, lng)) {
            const formatted = formatCoordinates(lat, lng);
            setCoordinates(formatted);
            if (onChange) {
                onChange(formatted);
            }
        }
    };

    const handleCoordinateSelect = (newCoordinates) => {
        setCoordinates(newCoordinates);
    };

    const handleGetCurrentLocation = () => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const { latitude, longitude } = position.coords;
                    const formatted = formatCoordinates(latitude, longitude);
                    setCoordinates(formatted);
                    if (onChange) {
                        onChange(formatted);
                    }
                },
                (error) => {
                    console.error('Error getting location:', error);
                    alert('No se pudo obtener la ubicación actual. Por favor habilite los permisos.');
                }
            );
        } else {
            alert('La geolocalización no está soportada por este navegador.');
        }
    };

    const getHelperText = () => {
        if (!coordinates) {
            return helperText || 'Ej: -4.9038, -80.6851 (Sullana) o pegue un enlace de Google Maps';
        }
        if (isValid) {
            return '✓ Coordenadas válidas y formateadas correctamente';
        }
        return '⚠ Formato inválido. Puede pegar coordenadas en DMS (Grados, Minutos), enlace de Google Maps o número con coma decimal.';
    };

    return (
        <>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <TextField
                    fullWidth
                    label={label}
                    value={coordinates}
                    onChange={handleCoordinateChange}
                    onBlur={handleBlur}
                    placeholder="Ej: -4.903800, -80.685100"
                    helperText={getHelperText()}
                    error={coordinates ? !isValid : false}
                    color={coordinates ? (isValid ? 'success' : 'error') : 'primary'}
                    sx={{
                        fontFamily: "'Outfit', sans-serif",
                        '& .MuiFormHelperText-root': {
                            color: coordinates ? (isValid ? '#10b981' : '#ef4444') : 'text.secondary',
                            fontWeight: coordinates ? 500 : 400
                        }
                    }}
                    InputProps={{
                        endAdornment: (
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                <Tooltip title="Obtener coordenadas de mi ubicación actual">
                                    <IconButton onClick={handleGetCurrentLocation} size="small" color="primary">
                                        <MyLocationIcon sx={{ fontSize: 20 }} />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Seleccionar visualmente en el mapa interactivo">
                                    <IconButton onClick={handleOpenDialog} size="small" color="primary">
                                        <AddIcon sx={{ fontSize: 22 }} />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        )
                    }}
                />
            </Box>

            <Dialog
                open={dialogOpen}
                onClose={handleCloseDialog}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 3, p: 1 }
                }}
            >
                <DialogTitle sx={{ pb: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6" sx={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}>
                            Localizador y Selector de Coordenadas
                        </Typography>
                        <IconButton onClick={handleCloseDialog} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ pt: 1 }}>
                    <Box sx={{ my: 1 }}>
                        <TextField
                            fullWidth
                            label="Coordenadas Geográficas (Latitud, Longitud)"
                            value={coordinates}
                            onChange={handleCoordinateChange}
                            onBlur={handleBlur}
                            placeholder="Ej: -4.903800, -80.685100"
                            error={coordinates ? !isValid : false}
                            color={coordinates ? (isValid ? 'success' : 'error') : 'primary'}
                            helperText={getHelperText()}
                            sx={{ mb: 2, fontFamily: "'Outfit', sans-serif" }}
                        />

                        <Button
                            variant="outlined"
                            startIcon={<MyLocationIcon />}
                            onClick={handleGetCurrentLocation}
                            sx={{ mb: 2, borderRadius: 2, fontFamily: "'Outfit', sans-serif" }}
                        >
                            Usar mi ubicación actual
                        </Button>

                        <Typography variant="body2" color="text.secondary" sx={{ display: 'block', mb: 2, fontFamily: "'Outfit', sans-serif", fontSize: '13px' }}>
                            📍 Haz clic en cualquier parte del mapa para fijar el marcador, o arrastra la mira telescópica morada para calibrar con precisión milimétrica.
                        </Typography>

                        <GeographicMap
                            mode="selection"
                            selectedCoordinates={coordinates}
                            onCoordinateSelect={handleCoordinateSelect}
                            height="400px"
                            hideHeader={true}
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={handleCloseDialog} sx={{ fontFamily: "'Outfit', sans-serif", borderRadius: 2 }}>
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleAcceptCoordinates}
                        variant="contained"
                        color="primary"
                        disabled={coordinates ? !isValid : true}
                        startIcon={<CheckIcon />}
                        sx={{ fontFamily: "'Outfit', sans-serif", borderRadius: 2, px: 2.5 }}
                    >
                        Confirmar Ubicación
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default CoordinateSelector;